import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, CategoryData } from '@/mocks/categories';
import { tournaments } from '@/mocks/home';

const gameSlugMap: Record<string, string> = {
  '英雄联盟': 'lol',
  'CS2': 'cs2',
  'DOTA2': 'dota2',
  '王者荣耀': 'hok',
  'Valorant': 'valorant',
  'PUBG': 'pubg',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<string>(slug || 'all');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const displayCategories = categories.filter((c) => c.slug !== 'news');

  const filteredTournaments = activeCategory === 'all'
    ? tournaments
    : tournaments.filter((t) => {
        const mapped = gameSlugMap[t.game];
        return mapped === activeCategory;
      });

  const isNews = activeCategory === 'news';

  const categorySchemas: Record<string, unknown> = categories.reduce((acc, cat) => {
    acc[cat.slug] = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${cat.name} - 开云电竞`,
      description: cat.description,
      url: `https://www.kaiyunesports.com/category/${cat.slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '开云电竞',
        url: 'https://www.kaiyunesports.com',
      },
    };
    return acc;
  }, {} as Record<string, unknown>);

  return (
    <>
      <SEOHead
        title={currentCategory ? `${currentCategory.name} - 开云电竞 | 专业电竞竞技平台` : '赛事中心 - 开云电竞'}
        description={currentCategory?.description || '浏览所有电竞赛事类别，一站式获取全球电竞最新动态与赛事信息'}
        keywords={`开云电竞,${currentCategory?.name || '赛事中心'},电竞竞技平台,电竞赛事`}
        canonicalUrl={`https://www.kaiyunesports.com/category/${activeCategory}`}
        schemaData={categorySchemas[activeCategory]}
      />

      <div className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: currentCategory?.name || '赛事中心' },
          ]}
        />

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
                {currentCategory?.name || '赛事中心'}
              </h1>
              <p className="text-foreground-600 text-sm mt-3 max-w-2xl mx-auto">
                {currentCategory?.description}
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {displayCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {!isNews && (
              <>
                {filteredTournaments.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filteredTournaments.map((t) => (
                      <Link
                        key={t.id}
                        to={`/category/${activeCategory}`}
                        className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-200"
                      >
                        <div className="relative h-44 md:h-48 overflow-hidden">
                          <img
                            src={t.image}
                            alt={t.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute top-3 left-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                              t.status === 'live' ? 'bg-red-500 text-white' : 'bg-accent-500 text-background-50'
                            }`}>
                              {t.status === 'live' ? '进行中' : '即将开始'}
                            </span>
                          </div>
                          <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full">
                            <span className="text-white text-xs font-medium">{t.game}</span>
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                            {t.title}
                          </h3>
                          <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {t.date}</span>
                            <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {t.viewers}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              {t.teams.slice(0, 3).map((team, i) => (
                                <span key={i} className="px-2 py-0.5 bg-background-100 rounded text-xs text-foreground-700">{team}</span>
                              ))}
                            </div>
                            <span className="text-xs font-semibold text-accent-600">{t.prize}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500 text-sm">该分类暂无赛事</p>
                  </div>
                )}
              </>
            )}

            {isNews && (
              <div className="space-y-4">
                {tournaments.map((t, i) => (
                  <Link
                    key={t.id}
                    to={`/article/${t.slug}`}
                    className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={t.image}
                        alt={t.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs text-accent-600 font-medium">{t.game}</span>
                        <span className="text-xs text-foreground-400">{t.date}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2">
                        {t.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
                        查看"{t.title}"的详细信息，包括参赛战队、赛事规则、比赛日程等完整赛事资讯。
                      </p>
                      <span className="text-xs text-primary-500 font-medium group-hover:underline">
                        阅读全文
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}